
import { CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, MapPin, Clock, Briefcase, Users, BanknoteIcon, Calendar, UserPlus, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { currencyConfig, CurrencyCode } from "@/utils/currency";

interface JobHeaderProps {
  title: string;
  organization?: string;
  fullName?: string;
  logoUrl?: string;
  location: string;
  workPresence?: string;
  workArrangement?: string;
  yearsOfExperience?: string;
  seniority?: string;
  salaryRange?: {
    min?: number;
    max?: number;
  };
  salaryCurrency?: string;
  publishedDate?: string;
  applicantCount?: number;
  onBack?: () => void;
}

export const JobHeader = ({ 
  title, 
  organization, 
  fullName, 
  logoUrl,
  location,
  workPresence,
  workArrangement,
  yearsOfExperience,
  seniority,
  salaryRange,
  salaryCurrency = 'INR',
  publishedDate,
  applicantCount,
  onBack
}: JobHeaderProps) => {
  return (
    <CardHeader className="space-y-6">
      <div className="flex items-center space-x-4">
        {onBack && (
          <Button 
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground"
            onClick={onBack}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <div className="space-y-2 flex-1">
          <div className="flex items-start justify-between">
            <CardTitle className="text-3xl font-bold text-foreground">{title}</CardTitle>
            <Badge 
              variant="secondary" 
              className="dark:bg-pink-900 dark:text-pink-100 bg-pink-100 text-pink-800 hover:bg-pink-100"
            >
              New
            </Badge>
          </div>
          <div className="flex items-center gap-3 text-lg">
            <Avatar className="h-10 w-10">
              <AvatarImage src={logoUrl} alt={organization || fullName} />
              <AvatarFallback className="bg-muted text-muted-foreground">
                {(organization || fullName || 'C')
                  .split(' ')
                  .map(word => word[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)}
              </AvatarFallback>
            </Avatar>
            <span className="text-muted-foreground">{organization || fullName}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {publishedDate && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4 shrink-0" />
            <span>Posted {publishedDate}</span>
          </div>
        )}
        {applicantCount !== undefined && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <UserPlus className="h-4 w-4 shrink-0" />
            <span>{applicantCount} {applicantCount === 1 ? 'applicant' : 'applicants'}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-muted-foreground">
          <MapPin className="h-4 w-4 shrink-0" />
          <span>{location}</span>
        </div>
        {workPresence && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Briefcase className="h-4 w-4 shrink-0" />
            <span>{workPresence}</span>
          </div>
        )}
        {workArrangement && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4 shrink-0" />
            <span>{workArrangement}</span>
          </div>
        )}
        {(yearsOfExperience || seniority) && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-4 w-4 shrink-0" />
            <span>{yearsOfExperience ? `${yearsOfExperience}+ years` : seniority}</span>
          </div>
        )}
        {(salaryRange?.min || salaryRange?.max) && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <BanknoteIcon className="h-4 w-4 shrink-0" />
            <span>
              {salaryRange.min && salaryRange.max
                ? `${salaryRange.min.toLocaleString()} - ${salaryRange.max.toLocaleString()} ${salaryCurrency}/year`
                : salaryRange.min
                ? `Min ${salaryRange.min.toLocaleString()} ${salaryCurrency}/year`
                : `Up to ${salaryRange.max?.toLocaleString()} ${salaryCurrency}/year`}
            </span>
          </div>
        )}
      </div>
    </CardHeader>
  );
};
